import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import BudgetRing from "@/components/BudgetRing";
import RiskGauge from "@/components/RiskGauge";
import CommandCenter from "@/components/CommandCenter";
import SymptomIntakeCard from "@/components/encounter/SymptomIntakeCard";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { ArrowUpRight, Pulse, HeartStraight, TestTube, Drop, Pill } from "@phosphor-icons/react";

const chartAxis = { stroke: "#A3ACA7", fontSize: 11 };

// Round `raw` UP to a friendly axis tick (1/2/5 × 10^n) so ticks never overlap.
// e.g. 3.4 → 4, 47 → 50, 165 → 200, 1200 → 1500.
function niceCeil(raw) {
  if (!raw || raw <= 0) return 10;
  const exp = Math.floor(Math.log10(raw));
  const base = Math.pow(10, exp);
  const norm = raw / base;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return nice * base;
}
function niceDomain(values, floorZero = true) {
  if (!values.length) return { domain: [0, 10], ticks: [0, 2.5, 5, 7.5, 10] };
  const maxV = Math.max(...values);
  const top = niceCeil(maxV * 1.15);
  const lo = floorZero ? 0 : Math.max(0, Math.floor(Math.min(...values) * 0.9));
  const step = (top - lo) / 4;
  return { domain: [lo, top], ticks: [lo, lo + step, lo + 2 * step, lo + 3 * step, top] };
}

function ChartCard({ title, subtitle, data, dataKey, color = "#4ADE80", testId }) {
  const values = data.map((d) => Number(d[dataKey])).filter((v) => !Number.isNaN(v));
  const { domain, ticks } = niceDomain(values);
  return (
    <div className="ros-card md:col-span-6 lg:col-span-3" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="ros-label">{title}</div>
          <div className="text-xs text-[#A3ACA7] mt-1">{subtitle}</div>
        </div>
      </div>
      <div className="mt-4 h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 12, bottom: 4, left: -4 }}>
            <CartesianGrid stroke="#1C201E" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" {...chartAxis} tickLine={false} axisLine={false}
                   tickFormatter={(v) => (v || "").slice(5)} minTickGap={24} />
            <YAxis {...chartAxis} tickLine={false} axisLine={false} width={48}
                   domain={domain} ticks={ticks} allowDecimals={false}
                   tickFormatter={(v) => Math.round(v).toString()} />
            <Tooltip
              contentStyle={{ background: "#121514", border: "1px solid #272E2B", borderRadius: 12, color: "#fff" }}
              labelStyle={{ color: "#A3ACA7", fontSize: 11 }}
            />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={3} dot={{ r: 3, fill: color }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isPatient = user?.role === "patient";
  const [egfrReadings, setEgfr] = useState([]);
  const [acrReadings, setAcr] = useState([]);
  const [dietSummary, setDietSummary] = useState(null);
  const [kfre, setKfre] = useState(null);
  const [profile, setProfile] = useState(null);
  const [weightData, setWeightData] = useState(null); // { readings, latest_alert }
  const [meds, setMeds] = useState([]);

  useEffect(() => {
    (async () => {
      const [egfrRes, acrRes, dietRes, profRes, weightRes, medsRes] = await Promise.all([
        api.get("/tracker/egfr"),
        api.get("/tracker/acr"),
        api.get("/diet/summary"),
        api.get("/patient/profile"),
        api.get("/tracker/weight"),
        api.get("/meds"),
      ]);
      setEgfr(egfrRes.data);
      setAcr(acrRes.data);
      setDietSummary(dietRes.data);
      setProfile(profRes.data);
      setWeightData(weightRes.data);
      setMeds(medsRes.data);

      if (egfrRes.data.length && acrRes.data.length && profRes.data) {
        const lastE = egfrRes.data[egfrRes.data.length - 1];
        const lastA = acrRes.data[acrRes.data.length - 1];
        const k = await api.post("/calc/kfre", {
          age: profRes.data.age,
          sex: profRes.data.sex,
          egfr: lastE.egfr,
          acr: lastA.acr,
        });
        setKfre(k.data);
      }
    })();
  }, []);

  const latestEgfr = egfrReadings[egfrReadings.length - 1];
  const latestAcr = acrReadings[acrReadings.length - 1];

  const stageInfo = latestEgfr
    ? latestEgfr.egfr >= 90
      ? { stage: "G1", label: "Normal", color: "#4ADE80" }
      : latestEgfr.egfr >= 60
      ? { stage: "G2", label: "Mildly ↓", color: "#4ADE80" }
      : latestEgfr.egfr >= 45
      ? { stage: "G3a", label: "Mild-moderate", color: "#FBBF24" }
      : latestEgfr.egfr >= 30
      ? { stage: "G3b", label: "Moderate-severe", color: "#FBBF24" }
      : latestEgfr.egfr >= 15
      ? { stage: "G4", label: "Severe", color: "#F87171" }
      : { stage: "G5", label: "Failure", color: "#F87171" }
    : null;

  return (
    <div className="space-y-6">
      <CommandCenter />
      {isPatient && <SymptomIntakeCard defaultSpecialty="general" />}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6" data-testid="dashboard-grid">
      {/* Hero: eGFR score */}
      <div className="ros-card md:col-span-4 md:row-span-2 flex flex-col justify-between min-h-[280px]" data-testid="hero-egfr">
        <div className="flex items-start justify-between">
          <div>
            <div className="ros-label">Latest eGFR</div>
            <div className="text-xs text-[#A3ACA7] mt-1">CKD-EPI 2021 · race-free</div>
          </div>
          {stageInfo && (
            <div className="px-3 py-1.5 rounded-full border border-[#272E2B] text-xs flex items-center gap-2"
                 style={{ color: stageInfo.color }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: stageInfo.color }} />
              Stage {stageInfo.stage} · {stageInfo.label}
            </div>
          )}
        </div>

        <div className="flex items-end gap-6 mt-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="ros-metric text-7xl sm:text-8xl leading-none"
            data-testid="hero-egfr-value"
          >
            {latestEgfr ? latestEgfr.egfr.toFixed(1) : "—"}
          </motion.div>
          <div className="pb-4">
            <div className="text-sm text-[#A3ACA7]">mL/min/1.73m²</div>
            <div className="text-xs text-[#A3ACA7] mt-1">
              {latestEgfr ? `As of ${latestEgfr.date}` : ""}
            </div>
          </div>
        </div>

        {egfrReadings.length > 0 && (
          <div className="mt-6 h-[110px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={egfrReadings} margin={{ top: 4, right: 4, bottom: 0, left: -30 }}>
                <XAxis dataKey="date" hide />
                <YAxis hide domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{ background: "#121514", border: "1px solid #272E2B", borderRadius: 12, color: "#fff" }}
                  labelStyle={{ color: "#A3ACA7", fontSize: 11 }}
                />
                <Line type="monotone" dataKey="egfr" stroke="#4ADE80" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* KFRE Risk gauge */}
      <div className="ros-card md:col-span-2 flex flex-col items-center justify-center" data-testid="hero-kfre">
        <div className="ros-label self-start">5-year kidney failure risk</div>
        <div className="text-xs text-[#A3ACA7] self-start mt-1 mb-4">KFRE (Tangri 4-var)</div>
        <RiskGauge value={kfre ? kfre.risk_5yr : 0} label="Predicted risk" testId="kfre-gauge" />
        {kfre && (
          <div className="text-xs text-[#A3ACA7] mt-3 self-start">
            2-year risk: <span className="text-white font-semibold">{kfre.risk_2yr.toFixed(2)}%</span>
          </div>
        )}
      </div>

      {/* Latest ACR */}
      <div className="ros-card md:col-span-2" data-testid="hero-acr">
        <div className="ros-label">Latest ACR</div>
        <div className="flex items-end gap-3 mt-4">
          <div className="ros-metric text-5xl">{latestAcr ? latestAcr.acr : "—"}</div>
          <div className="text-xs text-[#A3ACA7] pb-2">mg/g</div>
        </div>
        {latestAcr && (
          <div className="text-xs mt-2"
               style={{ color: latestAcr.acr < 30 ? "#4ADE80" : latestAcr.acr <= 300 ? "#FBBF24" : "#F87171" }}>
            {latestAcr.acr < 30 ? "A1 · Normal to mild" : latestAcr.acr <= 300 ? "A2 · Moderately increased" : "A3 · Severely increased"}
          </div>
        )}
        <Link to="/calculators" className="mt-4 inline-flex items-center gap-1 text-xs text-[#4ADE80]"
              data-testid="acr-cta">
          Log new reading <ArrowUpRight size={12} />
        </Link>
      </div>

      {/* Weight / fluid overload tile */}
      <div className="ros-card md:col-span-2" data-testid="hero-weight">
        <div className="flex items-center justify-between">
          <div>
            <div className="ros-label">Dry weight</div>
            <div className="text-xs text-[#A3ACA7] mt-1">Morning check-in · fluid tracker</div>
          </div>
          <Drop size={20} className="text-[#60A5FA]" />
        </div>
        {(() => {
          const list = weightData?.readings || [];
          const latest = list.length ? list[list.length - 1] : null;
          const alert = weightData?.latest_alert?.flag || null;
          return (
            <>
              <div className="flex items-end gap-3 mt-4">
                <div className="ros-metric text-5xl">{latest ? latest.weight_kg.toFixed(1) : "—"}</div>
                <div className="text-xs text-[#A3ACA7] pb-2">kg</div>
              </div>
              {alert ? (
                <div className={`text-xs mt-2 leading-relaxed ${alert.level === "high" ? "text-[#F87171]" : "text-[#FBBF24]"}`}
                     data-testid="weight-alert-banner">
                  {alert.level === "high" ? "⚠ Possible fluid retention · " : "· "}
                  {weightData.latest_alert.delta_kg >= 0 ? "+" : ""}{weightData.latest_alert.delta_kg} kg overnight
                </div>
              ) : latest ? (
                <div className="text-xs text-[#4ADE80] mt-2">Stable · no fluid warning</div>
              ) : null}
              <Link to="/weight" className="mt-4 inline-flex items-center gap-1 text-xs text-[#60A5FA]"
                    data-testid="weight-cta">
                Log this morning&apos;s weight <ArrowUpRight size={12} />
              </Link>
            </>
          );
        })()}
      </div>

      {/* Refill reminders tile — shows count of prescribed meds due_soon/overdue */}
      {(() => {
        const prescribed = meds.filter((m) => m.refill_status);
        const overdue = prescribed.filter((m) => m.refill_status.status === "overdue");
        const dueSoon = prescribed.filter((m) => m.refill_status.status === "due_soon");
        if (prescribed.length === 0) return null;
        const critical = overdue.length > 0;
        const warning = !critical && dueSoon.length > 0;
        const color = critical ? "#F87171" : warning ? "#FBBF24" : "#4ADE80";
        return (
          <div className="ros-card md:col-span-4" data-testid="hero-refills">
            <div className="flex items-center justify-between">
              <div>
                <div className="ros-label">Prescription refills</div>
                <div className="text-xs text-[#A3ACA7] mt-1">
                  {prescribed.length} active {prescribed.length === 1 ? "prescription" : "prescriptions"}
                </div>
              </div>
              <Pill size={20} style={{ color }} />
            </div>
            <div className="mt-4 flex items-baseline gap-4">
              <div className="ros-metric text-5xl" style={{ color }}>
                {critical ? overdue.length : warning ? dueSoon.length : "✓"}
              </div>
              <div className="text-xs text-[#A3ACA7]">
                {critical
                  ? `${overdue.length} overdue · action needed`
                  : warning
                  ? `${dueSoon.length} refill${dueSoon.length > 1 ? "s" : ""} due within 3 days`
                  : "All prescriptions on schedule"}
              </div>
            </div>
            <Link to="/medications" className="mt-4 inline-flex items-center gap-1 text-xs" style={{ color }}
                  data-testid="refills-cta">
              Review medications <ArrowUpRight size={12} />
            </Link>
          </div>
        );
      })()}

      {/* Chart: eGFR trend */}
      <ChartCard title="eGFR trend" subtitle="Rolling readings"
                 data={egfrReadings} dataKey="egfr" testId="chart-egfr" />

      {/* Chart: ACR trend */}
      <ChartCard title="ACR trend" subtitle="mg/g" color="#FBBF24"
                 data={acrReadings} dataKey="acr" testId="chart-acr" />

      {/* Diet rings */}
      <div className="ros-card md:col-span-4" data-testid="diet-rings">
        <div className="flex items-center justify-between">
          <div>
            <div className="ros-label">Today&apos;s renal diet</div>
            <div className="text-xs text-[#A3ACA7] mt-1">
              {dietSummary ? `${dietSummary.entries} entries logged` : "Loading…"}
            </div>
          </div>
          <Link to="/diet" className="text-xs text-[#4ADE80] inline-flex items-center gap-1"
                data-testid="diet-cta">
            Open optimizer <ArrowUpRight size={12} />
          </Link>
        </div>

        {dietSummary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <BudgetRing value={dietSummary.totals.sodium} budget={dietSummary.budgets.sodium}
                        color="#FCD34D" label="Sodium" unit="mg" testId="ring-sodium"
                        source={dietSummary.targets?.sodium?.source} guidelineValue={dietSummary.targets?.sodium?.guideline_value} />
            <BudgetRing value={dietSummary.totals.potassium} budget={dietSummary.budgets.potassium}
                        color="#F472B6" label="Potassium" unit="mg" testId="ring-potassium"
                        source={dietSummary.targets?.potassium?.source} guidelineValue={dietSummary.targets?.potassium?.guideline_value} />
            <BudgetRing value={dietSummary.totals.phosphorus} budget={dietSummary.budgets.phosphorus}
                        color="#A78BFA" label="Phosphorus" unit="mg" testId="ring-phosphorus"
                        source={dietSummary.targets?.phosphorus?.source} guidelineValue={dietSummary.targets?.phosphorus?.guideline_value} />
            <BudgetRing value={dietSummary.totals.protein} budget={dietSummary.budgets.protein}
                        color="#818CF8" label="Protein" unit="g" testId="ring-protein"
                        source={dietSummary.targets?.protein?.source} guidelineValue={dietSummary.targets?.protein?.guideline_value} />
          </div>
        )}
      </div>

      {/* Profile / stats card */}
      <div className="ros-card md:col-span-2 flex flex-col justify-between" data-testid="profile-card">
        <div>
          <div className="ros-label">Patient profile</div>
          {profile && (
            <>
              <div className="font-heading text-2xl mt-3">{profile.name}</div>
              <div className="text-xs text-[#A3ACA7] mt-1">
                {profile.age} y · {profile.sex} · {profile.weight_kg} kg
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {profile.conditions.map((c) => (
                  <span key={c} className="text-[11px] px-2 py-1 rounded-full border border-[#272E2B] text-[#A3ACA7]">
                    {c}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="mt-6 grid grid-cols-3 gap-2">
          <div className="ros-card-raised text-center">
            <Pulse size={16} className="text-[#4ADE80] mx-auto" />
            <div className="text-xs text-[#A3ACA7] mt-2">Stage</div>
            <div className="font-heading">{stageInfo?.stage || "—"}</div>
          </div>
          <div className="ros-card-raised text-center">
            <HeartStraight size={16} className="text-[#F472B6] mx-auto" />
            <div className="text-xs text-[#A3ACA7] mt-2">Age</div>
            <div className="font-heading">{profile?.age || "—"}</div>
          </div>
          <div className="ros-card-raised text-center">
            <TestTube size={16} className="text-[#FBBF24] mx-auto" />
            <div className="text-xs text-[#A3ACA7] mt-2">Labs</div>
            <div className="font-heading">{egfrReadings.length}</div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
